import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import { AuthService } from 'src/app/services/auth-service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { MyProfileApiService } from 'src/app/data-transfer/services/my-profile-api-service';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { ChangePasswordDto } from 'src/app/data-transfer/entities/change-password-dto';
import { PASSWORD_PROVIDER_NAME } from 'src/app/data-transfer/entities/login-method-dto';
import { LanguageHandler } from 'src/app/util/language-handler';
import { getApiErrorMessage } from '../admin-dashboard/admin-api-error';
import { getUserDisplayName } from '../admin-dashboard/organization-user.util';

interface MyGroupRow {
  id: string;
  name: string;
  members: { id: string; name: string }[];
}

@Component({
  selector: 'app-my-profile',
  templateUrl: './my-profile.component.html',
  styleUrls: ['./my-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyProfileComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly userApiService = inject(UserApiService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translateService = inject(TranslocoService);
  private readonly myProfileApiService = inject(MyProfileApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly languageHandler = new LanguageHandler(this.translateService);

  protected readonly organization = signal(new OrganizationDto());
  protected readonly isSso = signal<boolean | null>(null);
  protected readonly isEmailManaged = signal(false);
  protected readonly submittedSecurityForm = signal(false);
  protected readonly isSavingPersonalData = signal(false);
  protected readonly isLoadingProfile = signal(true);
  protected readonly isChangingPassword = signal(false);
  protected readonly myGroups = signal<MyGroupRow[]>([]);
  protected readonly countryCodes = this.languageHandler.getLanguageCodes();
  protected currentLanguageCode = this.languageCodeFromLang(this.translateService.getActiveLang());

  protected readonly personalDataForm = new FormGroup({
    firstName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    lastName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
  });

  protected readonly securityForm = new FormGroup({
    oldPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    newPassword: new FormControl<{ password: string; confirmPassword: string } | null>(null, Validators.required),
  });

  protected get isPremiumUser(): boolean {
    return this.authService.hasUserClaims() && this.authService.IsPremiumUser();
  }

  protected get roleLabelKey(): string {
    if (!this.authService.hasUserClaims()) return 'myProfile.organization.roles.member';
    if (this.authService.isLeadAdmin()) return 'myProfile.organization.roles.leadAdmin';
    return this.authService.isOrganizationAdmin() ? 'myProfile.organization.roles.admin' : 'myProfile.organization.roles.member';
  }

  ngOnInit(): void {
    this.personalDataForm.disable();
    this.myProfileApiService
      .getMyProfile(true)
      .pipe(
        finalize(() => this.isLoadingProfile.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: profile => {
          this.personalDataForm.reset({ firstName: profile.firstName ?? '', lastName: profile.lastName ?? '', email: profile.email });
          this.personalDataForm.enable();
          if (this.isEmailManaged()) {
            this.personalDataForm.controls.email.disable();
          }
        },
        error: error => this.snackbarService.showError(getApiErrorMessage(error)),
      });

    this.myProfileApiService
      .getMyUserGroups(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: groups =>
          this.myGroups.set(
            groups.map(group => ({
              id: group.id,
              name: group.name,
              members: group.members.map(member => ({ id: member.id, name: getUserDisplayName(member) || member.email })),
            }))
          ),
        error: error => this.snackbarService.showError(getApiErrorMessage(error)),
      });

    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      const organization = (data['ownOrganization'] as OrganizationDto) ?? new OrganizationDto();
      this.organization.set(organization);
    });

    this.authService
      .getLoginTypes(this.authService.getSignedInUsername())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(loginMethods => {
        const isSso = !loginMethods.some(method => method.providerName === PASSWORD_PROVIDER_NAME);
        this.isSso.set(isSso);
        if (isSso) {
          this.lockEmail();
        }
      });
  }

  private lockEmail(): void {
    this.personalDataForm.controls.email.disable();
    this.isEmailManaged.set(true);
  }

  protected setLanguage(countryCode: string): void {
    this.currentLanguageCode = countryCode;
    this.languageHandler.setLanguage(countryCode);
  }

  protected savePersonalData(): void {
    if (this.personalDataForm.invalid) {
      this.personalDataForm.markAllAsTouched();
      return;
    }
    this.isSavingPersonalData.set(true);
    this.myProfileApiService
      .updateMyProfile(this.personalDataForm.getRawValue(), true)
      .pipe(
        finalize(() => this.isSavingPersonalData.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.snackbarService.showInfo(this.translateService.translate('myProfile.personalData.saveSuccess'));
          void this.authService.reloadUserClaims();
        },
        error: error => this.snackbarService.showError(getApiErrorMessage(error)),
      });
  }

  protected changePassword(): void {
    this.submittedSecurityForm.set(true);
    if (this.securityForm.invalid) {
      return;
    }

    const { oldPassword, newPassword } = this.securityForm.getRawValue();
    const dto: ChangePasswordDto = {
      login: this.authService.getSignedInUsername(),
      oldPassword,
      newPassword: newPassword?.password ?? '',
    };

    this.isChangingPassword.set(true);
    this.userApiService
      .changePassword(dto, true)
      .pipe(
        finalize(() => this.isChangingPassword.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.securityForm.reset();
          this.submittedSecurityForm.set(false);
          this.snackbarService.showInfo(this.translateService.translate('myProfile.security.changePasswordSuccess'));
        },
        error: error => this.snackbarService.showError(getApiErrorMessage(error)),
      });
  }

  private languageCodeFromLang(lang: string): string {
    return lang === 'en' ? 'gb' : lang;
  }
}
